import type {
	AgMultiFilterModel,
	AgNumberFilterModel,
	AgSetFilterModel,
	AgTextFilterModel
} from "~/helpers/ag-grid/agFilterModels";

import { isMultiModel, isNumberModel, isSetModel, isTextModel } from "~/helpers/ag-grid/agFilterModels";

export type ActiveSubmodel = AgNumberFilterModel | AgSetFilterModel | AgTextFilterModel;

export interface CombinedNumberConditions {
	conditions: AgNumberFilterModel[];
	filterType: "number";
	operator: "OR";
}

const NO_NUMBER_COL_IDS: ReadonlySet<string> = new Set();

export const activeSubmodels = (model: AgMultiFilterModel): ActiveSubmodel[] =>
	model.filterModels.filter((sub): sub is ActiveSubmodel => isTextModel(sub) || isNumberModel(sub) || isSetModel(sub));

export const composeActiveSubmodels = (colId: string, submodels: readonly unknown[]): Record<string, unknown> => {
	if (submodels.length === 0) return {};
	if (submodels.length === 1) return { [colId]: submodels[0] };
	return {
		[colId]: { condition1: submodels[0], condition2: submodels[1], conditions: [...submodels], operator: "AND" }
	};
};

const isNumberSetMember = (value: null | string): boolean => value == null || Number.isFinite(Number(value));

const toNumberCondition = (value: null | string): AgNumberFilterModel =>
	value == null
		? { filterType: "number", type: "blank" }
		: { filter: Number(value), filterType: "number", type: "equals" };

export const numberSetAsConditions = (model: AgSetFilterModel): AgSetFilterModel | CombinedNumberConditions => {
	const conditions = (model.values ?? []).filter(isNumberSetMember).map(toNumberCondition);
	return conditions.length > 0 ? { conditions, filterType: "number", operator: "OR" } : model;
};

const unwrapMultiFilterModel = (
	filterModel: unknown,
	numberColIds: ReadonlySet<string> = NO_NUMBER_COL_IDS
): unknown => {
	if (!filterModel || typeof filterModel !== "object") return filterModel ?? null;
	const unwrapped: Record<string, unknown> = {};
	for (const [colId, model] of Object.entries(filterModel as Record<string, unknown>)) {
		const toServerSubmodel = (sub: unknown): unknown =>
			numberColIds.has(colId) && isSetModel(sub) ? numberSetAsConditions(sub) : sub;
		if (isMultiModel(model)) {
			Object.assign(unwrapped, composeActiveSubmodels(colId, activeSubmodels(model).map(toServerSubmodel)));
		} else {
			unwrapped[colId] = toServerSubmodel(model);
		}
	}
	return unwrapped;
};

export default unwrapMultiFilterModel;
